import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import AthleteRegistrationForm from './pages/AthleteRegistrationForm';
import PaymentPage from './pages/PaymentPage';
import Login from './pages/Login';
import Success from './pages/Success';
import Dashboard from './pages/Dashboard';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import AdminLogin from './pages/AdminLogin';
import AthletesList from './pages/AthletesList';
import { ToastContainer } from 'react-toastify';
import StateAssociationRegistrationForm from './pages/StateAssociationRegistrationForm';
import StateAssociationSuccess from './pages/StateAssociationSuccess';
import AdminLayout from './components/layout/AdminLayout';
import StateAssociationsList from './pages/StateAssociationsList';
import AthleteEventsList from './pages/AthleteEventsList';
import StateAssociationLogin from './pages/StateAssociationLogin';
import StateAssociationDashboard from './pages/StateAssociationDashboard';
import EventApplicationsList from './pages/EventApplicationsList';
import UpgradePage from './pages/UpgradePage';
import UpgradeSuccess from './pages/UpgradeSuccess';

// Layout wrapper component to conditionally render Header and Footer
function Layout({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <>
      {!isAdminRoute && <Header />}
      {children}
      {!isAdminRoute && <Footer />}
    </>
  );
}


const theme = createTheme({
  palette: {
    primary: {
      main: '#291e6c',
      dark: '#1f1750',
    },
    secondary: {
      main: '#a70000',
    },
    error: {
      main: '#DB162A',
    },
    warning: {
      main: '#FEED00',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#2c3e50',
      secondary: '#7f8c8d',
    },
  },
  typography: {
    fontFamily: "'Oswald', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h2: {
      fontFamily: "'Oswald', sans-serif",
      fontWeight: 800,
    },
    h3: {
      fontFamily: "'Oswald', sans-serif",
      fontWeight: 700,
    },
    h5: {
      fontFamily: "'Oswald', sans-serif",
      fontWeight: 700,
    },
    h6: {
      fontFamily: "'Oswald', sans-serif",
      fontWeight: 600,
    },
    button: {
      fontFamily: "'Oswald', sans-serif",
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '1rem',
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

// Protected Route for Admin
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading: isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Protected Route for Athlete
const AthleteRoute = ({ children }) => {
  const { isAuthenticated, isAthlete, loading } = useAuth();
  console.log(isAuthenticated);
  console.log(isAthlete);
  if (loading) {
    return <div>Loading...</div>;
  }

  // if (!isAuthenticated) {
  //   return <Navigate to="/login" replace />;
  // }

  return children;
};

function App() {
useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    const handleKeyDown = (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        (e.ctrlKey && e.shiftKey && e.key === 'J') ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);


  return (
    <Router>
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
          <Toaster position="top-right" />
          
        <Layout>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Navigate to="/register" replace />} />
            <Route
              path="/register"
              element={<AthleteRegistrationForm />}
            />
            <Route path="/login" element={<Login />} />
            <Route path="/payment" element={<PaymentPage />} />
            
            <Route path="/success" element={<Success />} />
            <Route path="/state-association/register" element={<StateAssociationRegistrationForm />} />
            <Route path="/state-association/success" element={<StateAssociationSuccess />} />
            <Route path="/state-association/login" element={<StateAssociationLogin />} />
            <Route path="/state-association/dashboard" element={<StateAssociationDashboard />} />
            {/* Athlete Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <AthleteRoute>
                  <Dashboard />
                </AthleteRoute>
              }
            />
            <Route path="/upgrade" element={<AthleteRoute><UpgradePage /></AthleteRoute>} />
            <Route path="/upgrade-success" element={<AthleteRoute><UpgradeSuccess /></AthleteRoute>} />

            {/* Admin Routes */}
            {/* Admin Login (No Layout) */}
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* Admin Routes with Layout and Sidebar */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="athletes" element={<AthletesList />} />
              <Route path="state-associations" element={<StateAssociationsList />} />
              <Route path="athlete-events" element={<AthleteEventsList />} />
              <Route path="event-applications" element={<EventApplicationsList />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/register" replace />} />
          </Routes>
        </Layout>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      </ThemeProvider>
    </AuthProvider>
    </Router>
  );
}

export default App;